import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Select, Switch, Checkbox } from "antd";
import { Loader2, MailPlus, Plus, Save } from "lucide-react";
import { ContainerCard } from "../ui/cards";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../common/ToastMsg";
import { EMAIL_LEAD_WELCOME_URL, EMAIL_LEAD_WELCOME_LOG_URL } from "../../constants/api";
import { cn } from "../../utils/tailwindUtils";

const STATUS_STYLE = {
    pending: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
    sending: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
    sent:    'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
    skipped: 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400',
    failed:  'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
};
const STATUS_LABEL = { pending: 'Waiting', sending: 'Sending', sent: 'Sent', skipped: 'Not sent', failed: 'Failed' };

const fmt = (d) => (d ? new Date(d).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '');

// The endpoint also carries the WhatsApp welcome (LeadWelcomeWhatsAppCard);
// this card keeps and saves only the email's own fields.
const emailFields = ({ mailboxes, wa_enabled, wa_account_id, wa_template_id, wa_variables,
                       wa_accounts, wa_templates, wa_fields, ...rest }) => rest;

/**
 * Settings → Email Templates: the one template every new lead is sent,
 * a couple of minutes after it arrives. Backend: email/lead-welcome/
 * (platform_core/email_integration/lead_welcome.py). Off until switched on.
 */
export default function LeadWelcomeCard({ templates, onCreateTemplate }) {
    const { fetch, update } = useApiMethods();
    const navigate = useNavigate();
    const [form, setForm] = useState(null);
    const [mailboxes, setMailboxes] = useState([]);
    const [log, setLog] = useState([]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const load = async () => {
        const [cfg, recent] = await Promise.all([
            fetch(EMAIL_LEAD_WELCOME_URL), fetch(EMAIL_LEAD_WELCOME_LOG_URL),
        ]);
        if (cfg?.status === 'success') {
            setForm(emailFields(cfg.data));
            setMailboxes(cfg.data.mailboxes || []);
            setError('');
        } else {
            setError(cfg?.message || 'Could not load the welcome email settings');
        }
        if (recent?.status === 'success') setLog(recent.data || []);
    };

    useEffect(() => { load(); }, []);

    const save = async (patch = {}) => {
        const body = { ...form, ...patch };
        setSaving(true);
        const res = await update(EMAIL_LEAD_WELCOME_URL, body);
        setSaving(false);
        if (res?.status === 'success') {
            setForm(emailFields(res.data));
            setMailboxes(res.data.mailboxes || []);
            ToastMsg('success', patch.is_enabled === undefined ? 'Saved'
                : patch.is_enabled ? 'Welcome email switched on' : 'Welcome email switched off');
        } else {
            ToastMsg('error', res?.message || 'Save failed');
        }
    };

    if (error) {
        return (
            <ContainerCard>
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </ContainerCard>
        );
    }
    if (!form) {
        return (
            <ContainerCard>
                <div className="flex items-center gap-2 text-sm text-gray-400">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading welcome email…
                </div>
            </ContainerCard>
        );
    }

    const templateOptions = templates.map(t => ({ value: t.id, label: t.name }));
    const mailboxOptions = mailboxes.map(m => ({
        value: m.id,
        disabled: !m.can_send,
        label: `${m.email_address}${m.owner ? ` (${m.owner})` : ''}${m.can_send ? '' : ' - SMTP not verified'}`,
    }));
    const chosen = templates.find(t => t.id === form.template_id);

    return (
        <ContainerCard>
            <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
                <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-brand-50 dark:bg-brand-900/30 flex items-center justify-center shrink-0">
                        <MailPlus className="h-4 w-4 text-brand-600 dark:text-brand-300" />
                    </div>
                    <div>
                        <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">New lead welcome email</h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                            Every new lead with an email address gets the chosen template once, about two minutes after it arrives.
                            Imported sheets, existing customers and leads your team has already emailed are left out.
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600 dark:text-gray-300">{form.is_enabled ? 'On' : 'Off'}</span>
                    <Switch checked={form.is_enabled} loading={saving}
                            onChange={(v) => save({ is_enabled: v })} />
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <div className="flex items-center justify-between mb-1">
                        <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Template</label>
                        {onCreateTemplate && (
                            <button type="button" onClick={onCreateTemplate}
                                    className="flex items-center gap-1 text-xs font-medium text-brand-600 dark:text-brand-300 hover:underline">
                                <Plus className="h-3 w-3" /> New template
                            </button>
                        )}
                    </div>
                    <Select className="w-full" placeholder="Choose a template" allowClear showSearch
                            optionFilterProp="label" value={form.template_id ?? undefined}
                            options={templateOptions}
                            notFoundContent={<span className="text-xs text-gray-500">No templates yet</span>}
                            dropdownRender={(menu) => (
                                <>
                                    {menu}
                                    {onCreateTemplate && (
                                        <button type="button" onClick={onCreateTemplate}
                                                className="w-full flex items-center gap-1.5 px-3 py-2 mt-1 border-t border-gray-100 dark:border-gray-700 text-xs font-medium text-brand-600 dark:text-brand-300 hover:bg-gray-50 dark:hover:bg-gray-800">
                                            <Plus className="h-3.5 w-3.5" /> Create a new template
                                        </button>
                                    )}
                                </>
                            )}
                            onChange={(v) => setForm(p => ({ ...p, template_id: v ?? null }))} />
                    {templates.length === 0 && (
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                            Create a template first, then choose it here.
                        </p>
                    )}
                    {chosen && (
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1 line-clamp-1">
                            Subject: {chosen.subject}
                        </p>
                    )}
                </div>
                <div>
                    <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block">Send from</label>
                    <Select className="w-full" placeholder="Choose a mailbox" allowClear
                            value={form.sender_account_id ?? undefined} options={mailboxOptions}
                            onChange={(v) => setForm(p => ({ ...p, sender_account_id: v ?? null }))} />
                    <Checkbox className="mt-2" checked={form.send_from_owner}
                              onChange={(e) => setForm(p => ({ ...p, send_from_owner: e.target.checked }))}>
                        <span className="text-xs text-gray-600 dark:text-gray-300">
                            Use the lead owner's own mailbox when they have one
                        </span>
                    </Checkbox>
                </div>
            </div>

            <div className="mt-4">
                <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block">
                    Details page URL <span className="font-normal text-gray-400">(for {'{{verification_link}}'})</span>
                </label>
                <Input value={form.profile_page_url || ''} placeholder="https://emedhub.in/confirm/"
                       onChange={(e) => setForm(p => ({ ...p, profile_page_url: e.target.value }))} />
                <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                    Put {'{{verification_link}}'} in the template and each lead gets a personal link to this page,
                    where they can check and update their details. The WhatsApp welcome below uses the same page.
                    Leave blank if neither message has a link.
                </p>
            </div>

            <div className="flex justify-end mt-4">
                <Button onClick={() => save()} disabled={saving} className="text-sm flex items-center gap-2">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
                </Button>
            </div>

            <div className="mt-6">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">Recent leads</h3>
                {log.length === 0 ? (
                    <p className="text-xs text-gray-400">No new leads since the welcome email was switched on.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left border-b border-gray-200 dark:border-gray-700">
                                    <th className="pb-2 pr-4 text-xs font-semibold text-gray-500 dark:text-gray-400">Lead</th>
                                    <th className="pb-2 pr-4 text-xs font-semibold text-gray-500 dark:text-gray-400">Status</th>
                                    <th className="pb-2 pr-4 text-xs font-semibold text-gray-500 dark:text-gray-400">To</th>
                                    <th className="pb-2 text-xs font-semibold text-gray-500 dark:text-gray-400">When</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                                {log.map(r => (
                                    <tr key={r.id}>
                                        <td className="py-2 pr-4">
                                            <button type="button" className="text-brand-600 dark:text-brand-300 hover:underline text-left"
                                                    onClick={() => navigate(`/customers/customerdetails?id=${r.customer_id}`,
                                                        { state: { formEdit: true, row: { id: r.customer_id } } })}>
                                                {r.customer_name || `Lead #${r.customer_id}`}
                                            </button>
                                        </td>
                                        <td className="py-2 pr-4">
                                            <span className={cn("px-2 py-0.5 text-[10px] font-medium rounded-full", STATUS_STYLE[r.status])}>
                                                {STATUS_LABEL[r.status] || r.status}
                                            </span>
                                            {r.reason && <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">{r.reason}</span>}
                                        </td>
                                        <td className="py-2 pr-4 text-xs text-gray-600 dark:text-gray-300">{r.to_email || '—'}</td>
                                        <td className="py-2 text-xs text-gray-500 dark:text-gray-400">{fmt(r.sent_at || r.created_date)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </ContainerCard>
    );
}
